import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { useProject } from '../contexts/ProjectContext';

const STORAGE_KEY_PREFIX = 'zoneqr_data_';

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  zone: '',
  viewpoint: '',
  link: '',
});

const ZoneQR = () => {
  const { projectId } = useParams();
  const { projects: contextProjects, defaultProject, isValidProject, projectsLoaded } = useProject();
  const initialProject = projectId && isValidProject(projectId) ? projectId : defaultProject;
  const [activeProject, setActiveProject] = useState(initialProject);
  const hasSyncedDefault = useRef(false);
  const hasUserSelected = useRef(false);

  const [rows, setRows] = useState([createEmptyRow()]);
  const [batchExporting, setBatchExporting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const fileInputRef = useRef(null);

  const projects = contextProjects;

  const storageKey = `${STORAGE_KEY_PREFIX}${activeProject}`;

  // Sync default project once loaded
  useEffect(() => {
    if (projectsLoaded && !hasSyncedDefault.current && !projectId && !hasUserSelected.current) {
      setActiveProject(defaultProject);
      hasSyncedDefault.current = true;
    }
  }, [projectsLoaded, defaultProject, projectId]);

  // Load rows from localStorage — only once the project is fully resolved
  useEffect(() => {
    if (!projectsLoaded) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setRows(parsed);
          return;
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    setRows([createEmptyRow()]);
  }, [storageKey, projectsLoaded]);

  // Persist rows to localStorage — only once the project is fully resolved
  useEffect(() => {
    if (!projectsLoaded) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(rows));
    } catch (e) {
      // ignore storage errors
    }
  }, [rows, storageKey, projectsLoaded]);

  const handleProjectChange = useCallback((e) => {
    hasUserSelected.current = true;
    setActiveProject(e.target.value);
  }, []);

  const getProjectPath = (path) => `/${activeProject}${path}`;

  const handleCellChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleDeleteRow = (id) => {
    setRows((prev) => {
      const next = prev.filter((row) => row.id !== id);
      return next.length > 0 ? next : [createEmptyRow()];
    });
  };

  // Import rows from Excel (columns: Zone, Viewpoint, Link)
  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!rawData || rawData.length === 0) {
          alert('No data found in the Excel file.');
          return;
        }

        // Normalise header keys (case-insensitive, trim whitespace)
        const normalise = (obj) => {
          const out = {};
          Object.keys(obj).forEach((k) => { out[k.trim().toLowerCase()] = obj[k]; });
          return out;
        };

        const imported = rawData
          .map(normalise)
          .filter((r) => r.zone || r.viewpoint || r.link)
          .map((r) => ({
            id: Date.now() + Math.random(),
            zone: String(r.zone ?? '').trim(),
            viewpoint: String(r.viewpoint ?? '').trim(),
            link: String(r.link ?? '').trim(),
          }));

        if (imported.length === 0) {
          alert('No valid rows found. Make sure the Excel has columns named Zone, Viewpoint, and Link.');
          return;
        }

        const confirmReplace = window.confirm(
          `Import ${imported.length} row(s) from "${file.name}"?\n\nClick OK to replace current data, or Cancel to append.`
        );
        setRows(confirmReplace ? imported : (prev) => [...prev, ...imported]);
        alert(`Successfully imported ${imported.length} row(s).`);
      } catch (err) {
        console.error('Excel import error:', err);
        alert('Failed to read the Excel file. Please check the file format.');
      }
    };
    reader.readAsBinaryString(file);

    // Reset so the same file can be re-imported
    e.target.value = '';
  };

  // Generate QR code for a single row (opens new window)
  const handleViewQR = (row) => {
    const link = row.link.trim();
    if (!link) {
      alert('Please enter a Link for this zone before viewing the QR code.');
      return;
    }

    const label = row.zone.trim() || link;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=jpg&data=${encodeURIComponent(link)}`;

    const qrWindow = window.open('', '_blank', 'width=500,height=620');
    qrWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${label}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            background-color: #f5f5f5;
            padding: 20px;
            box-sizing: border-box;
          }
          .container {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            text-align: center;
            max-width: 400px;
          }
          canvas {
            border: 2px solid #ddd;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .download-btn {
            margin-top: 20px;
            padding: 10px 20px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14px;
            text-decoration: none;
            display: inline-block;
          }
          .download-btn:hover { background-color: #0056b3; }
          .info {
            margin-top: 15px;
            font-size: 12px;
            color: #666;
            line-height: 1.4;
            word-break: break-all;
          }
          .loading { color: #666; font-style: italic; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="loading">Generating QR code...</div>
          <canvas id="qrCanvas" width="350" height="400" style="display:none;"></canvas>
          <div id="downloadSection" style="display:none;">
            <a id="downloadBtn" class="download-btn">Download Image</a>
            <div class="info">
              <p>URL: ${link}</p>
            </div>
          </div>
        </div>
        <script>
          const canvas = document.getElementById('qrCanvas');
          const ctx = canvas.getContext('2d');
          const downloadSection = document.getElementById('downloadSection');
          const downloadBtn = document.getElementById('downloadBtn');
          const loading = document.querySelector('.loading');

          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          const qrImg = new Image();
          qrImg.crossOrigin = 'anonymous';
          qrImg.onload = function() {
            const qrSize = 300;
            const qrX = (canvas.width - qrSize) / 2;
            const qrY = 20;
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

            ctx.fillStyle = 'black';
            ctx.font = 'bold 28px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('${label.replace(/'/g, "\\'")}', canvas.width / 2, qrY + qrSize + 40);

            loading.style.display = 'none';
            canvas.style.display = 'block';
            downloadSection.style.display = 'block';

            downloadBtn.onclick = function() {
              const link = document.createElement('a');
              link.download = 'QR_${label.replace(/'/g, "\\'")}.jpg';
              link.href = canvas.toDataURL('image/jpeg', 0.9);
              link.click();
            };
          };
          qrImg.onerror = function() {
            loading.textContent = 'Error loading QR code. Please try again.';
            loading.style.color = 'red';
          };
          qrImg.src = '${qrCodeUrl}';
        </script>
      </body>
      </html>
    `);
    qrWindow.document.close();
  };

  // Batch export all valid rows as a ZIP
  const handleBatchQRExport = async () => {
    const validRows = rows.filter((r) => r.link.trim());
    if (validRows.length === 0) {
      alert('No rows with a Link found. Please add links before exporting.');
      return;
    }

    setBatchExporting(true);
    setBatchProgress(0);

    try {
      const zip = new JSZip();
      const batchSize = 10;
      let completed = 0;

      for (let batchIndex = 0; batchIndex < Math.ceil(validRows.length / batchSize); batchIndex++) {
        const batch = validRows.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize);

        const batchPromises = batch.map(async (row) => {
          const link = row.link.trim();
          const label = row.zone.trim() || link;
          const filename = (row.zone.trim() || `zone_${row.id}`).replace(/[/\\?%*:|"<>]/g, '_');
          const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=jpg&data=${encodeURIComponent(link)}`;

          try {
            const response = await fetch(qrCodeUrl);
            if (!response.ok) throw new Error(`Failed to fetch QR for ${label}`);
            const qrBlob = await response.blob();

            const canvas = document.createElement('canvas');
            canvas.width = 350;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const qrImg = new Image();
            qrImg.crossOrigin = 'anonymous';

            return new Promise((resolve) => {
              qrImg.onload = () => {
                const qrSize = 300;
                const qrX = (canvas.width - qrSize) / 2;
                const qrY = 20;
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

                ctx.fillStyle = 'black';
                ctx.font = 'bold 28px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(label, canvas.width / 2, qrY + qrSize + 40);

                canvas.toBlob((blob) => {
                  if (blob) zip.file(`QR_${filename}.jpg`, blob);
                  resolve();
                }, 'image/jpeg', 0.9);
              };
              qrImg.onerror = () => resolve();
              qrImg.src = URL.createObjectURL(qrBlob);
            });
          } catch (err) {
            console.error(`Error processing zone ${label}:`, err);
          }
        });

        await Promise.all(batchPromises);
        completed += batch.length;
        setBatchProgress(Math.round((completed / validRows.length) * 100));

        if (batchIndex < Math.ceil(validRows.length / batchSize) - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Zone_QR_Codes_${activeProject.toUpperCase()}_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);

      alert(`Successfully exported ${validRows.length} Zone QR code(s)!`);
    } catch (error) {
      console.error('Error during zone QR batch export:', error);
      alert('Error during export. Please try again.');
    } finally {
      setBatchExporting(false);
      setBatchProgress(0);
    }
  };

  if (!projectsLoaded) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-900">
        <div className="text-lg text-white">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <nav className="bg-gray-900 shadow-lg border-b border-gray-700">
        <div className="container mx-auto px-4 py-3">
          {/* Mobile Navigation */}
          <div className="md:hidden">
            <div className="flex justify-center mb-3">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-8 w-auto" />
            </div>
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-bold text-white">HK Pavilion CMS</h1>
              <div className="flex space-x-1">
                <a href={getProjectPath('/books/booklist')} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Book List</a>
                <a href={getProjectPath('/companies')} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Companies</a>
                <a href={getProjectPath('/zones')} className="text-white font-semibold bg-blue-600 px-2 py-1 rounded text-xs">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Admin</a>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex justify-center mb-4">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-10 w-auto" />
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">HK Pavilion CMS</h1>
                <div className="relative">
                  <select
                    value={activeProject}
                    onChange={handleProjectChange}
                    className="bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-x-4">
                <a href={getProjectPath('/books/booklist')} className="text-gray-300 hover:text-white">Book List</a>
                <a href={getProjectPath('/companies')} className="text-gray-300 hover:text-white">Companies</a>
                <a href={getProjectPath('/zones')} className="text-white font-semibold bg-blue-600 px-3 py-1 rounded">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white">Admin</a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="min-h-screen bg-gray-900">
        <div className="w-full px-4 py-4">
          {/* Page header and controls */}
          <div className="mb-4">
            <div className="hidden md:block mb-3">
              <h1 className="text-2xl font-bold text-white">
                {projects.find((p) => p.id === activeProject)?.name} - Zone QR Codes
              </h1>
            </div>

            {/* Mobile project selector */}
            <div className="md:hidden mb-3">
              <select
                value={activeProject}
                onChange={handleProjectChange}
                className="bg-gray-800 text-white border border-gray-600 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 outline outline-2 outline-blue-600"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleAddRow}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  + Add Row
                </button>
                <button
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Import Excel
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportExcel}
                  className="hidden"
                />
                {batchExporting && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-green-400 text-sm font-medium">Exporting... {batchProgress}%</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleBatchQRExport}
                disabled={batchExporting}
                className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                  batchExporting
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {batchExporting ? 'Exporting...' : 'Export QR Codes'}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider w-8">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      Zone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      Viewpoint
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      Link
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider w-40">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {rows.map((row, index) => (
                    <tr key={row.id} className="hover:bg-gray-750 transition-colors">
                      <td className="px-4 py-3 text-gray-400 text-xs">{index + 1}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.zone}
                          onChange={(e) => handleCellChange(row.id, 'zone', e.target.value)}
                          placeholder="e.g. Zone A"
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.viewpoint}
                          onChange={(e) => handleCellChange(row.id, 'viewpoint', e.target.value)}
                          placeholder="e.g. Viewpoint 1"
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="url"
                          value={row.link}
                          onChange={(e) => handleCellChange(row.id, 'link', e.target.value)}
                          placeholder="https://..."
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewQR(row)}
                            title="View QR Code"
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors whitespace-nowrap"
                          >
                            View QR
                          </button>
                          <button
                            onClick={() => handleDeleteRow(row.id)}
                            title="Delete row"
                            className="px-2 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-gray-500 text-xs mt-3">
            Data is saved automatically in your browser. QR codes are generated from the Link column.
            The Zone name is displayed as a label below each QR code image.
          </p>
        </div>
      </div>
    </>
  );
};

export default ZoneQR;
